import type { GateCohortCode, PdrGateCalculation, PdrGateCalculationBucket } from "@/types";

import { toFormat } from "@/lib/dateHelpers";

function gateEventLabel(cohortCode: GateCohortCode): string {
  return cohortCode === "LM" ? "Life Member entry" : "Reinstatement";
}

function creditsLabel(bucket: PdrGateCalculationBucket): string {
  return bucket.stipulationCode === "TOTALPERCYCLE" ? "total credits" : `${bucket.label} credits`;
}

function earnedLabel(bucket: PdrGateCalculationBucket): string {
  if (bucket.stipulationCode === "TOTALPERCYCLE") {
    return "Credits earned through that date";
  }

  return "Credits earned through that date (ED/MT)";
}

export function buildGateCalculationTooltip(
  bucket: PdrGateCalculationBucket,
  calculation: PdrGateCalculation,
  gateCohortCode: GateCohortCode
): { title: string; supportingText: string } {
  const eventDate = toFormat(calculation.eventDate, "MMMM d, yyyy");
  const eventLabel = gateEventLabel(gateCohortCode);
  const owedLine = `At ${eventLabel} on ${eventDate}, the member owed ${bucket.priorNormalRequirement} ${creditsLabel(bucket)} under ${calculation.priorCohortDescription}.`;
  const prorationLine = `Proration credit for remaining time in cycle: ${bucket.prorationCredit}`;
  const earnedLine = `${earnedLabel(bucket)}: ${bucket.earnedAtEvent}`;
  const deficitLine = bucket.deficitAdded > 0 ? `Prior-cycle deficit added: ${bucket.deficitAdded}\n` : "";
  const calculatedObligation = Math.max(
    0,
    bucket.priorNormalRequirement - bucket.prorationCredit - bucket.earnedAtEvent
  );
  const frozenLine = `Frozen exit obligation: ${bucket.priorNormalRequirement} - ${bucket.prorationCredit} - ${bucket.earnedAtEvent} = ${calculatedObligation}`;

  return {
    title: "Gate obligation calculation",
    supportingText: [owedLine, prorationLine, earnedLine, deficitLine, frozenLine].filter(Boolean).join("\n")
  };
}

export function findGateCalculationBucket(
  calculation: PdrGateCalculation | null | undefined,
  stipulationCode?: string | null
): PdrGateCalculationBucket | undefined {
  if (!calculation || !stipulationCode) {
    return undefined;
  }

  return calculation.buckets.find((bucket) => bucket.stipulationCode === stipulationCode);
}
